import * as React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useState as useReactState } from 'react';
import {
    AREA_ICONS,
    IconBack,
    IconClose,
    IconMenu,
    IconSearch,
} from './icons';
import Find from './Find';
import '../v3.css';
import '../theme';
import smileMark from '../../smile_icon.png';

/*
 * Event-scope chrome (event-overview-concept.html v7: the inward room).
 * The app rail is gone; a minimal top bar carries the org mark and the
 * exit; the event rail floats edge-less: event card · QUICK LINKS
 * cluster · hairline · Overview + areas. Areas without a native v3
 * surface bridge out (README rule 2) — the live v2 event pages are the
 * ground for most of them. Mobile re-renders the rail as the sheet.
 */

export interface EventQuickLink {
    id: string;
    label: string;
    icon: React.ComponentType;
    /** absolute or app-relative URL; copyable when copy is true */
    href: string;
    copy?: boolean;
}

export interface EventNavItem {
    id: string;
    label: string;
    /** where the row lands; bridges point at the classic/v2 surface */
    to: string;
}

const CheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--good)" strokeWidth="2.5" aria-hidden="true">
        <path d="m5 12 4 4L19 6" />
    </svg>
);
const CopyIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="9" y="9" width="12" height="12" rx="2" />
        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
);
const OpenIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M14 4h6v6M20 4 9 15M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h6" />
    </svg>
);

function absolute(href: string): string {
    return href.startsWith('http')
        ? href
        : `${window.location.origin}${href}`;
}

function QuickLinkRow({
    link,
    onNavigate,
}: {
    link: EventQuickLink;
    onNavigate?: () => void;
}) {
    const Icon = link.icon;
    /* feedback lives on the trigger, not in a toast (guidelines: Design) */
    const [copied, setCopied] = useReactState(false);
    return (
        <div
            className="ql"
            title={link.label}
            aria-label={link.label}
            role="link"
            tabIndex={0}
            onClick={() => {
                onNavigate?.();
                window.open(absolute(link.href), '_blank');
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter')
                    window.open(absolute(link.href), '_blank');
            }}
        >
            <Icon />
            <span className="lbl">{link.label}</span>
            <span className="ops">
                {link.copy && (
                    <button
                        className="op"
                        aria-label={`Copy ${link.label} link`}
                        title={`Copy ${link.label} link`}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard?.writeText(
                                absolute(link.href),
                            );
                            setCopied(true);
                            window.setTimeout(
                                () => setCopied(false),
                                1200,
                            );
                        }}
                    >
                        {copied ? <CheckIcon /> : <CopyIcon />}
                    </button>
                )}
                <button
                    className="op"
                    aria-label={`Open ${link.label}`}
                    title={`Open ${link.label}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate?.();
                        window.open(absolute(link.href), '_blank');
                    }}
                >
                    <OpenIcon />
                </button>
            </span>
        </div>
    );
}

function QuickLinks({
    links,
    onNavigate,
}: {
    links: EventQuickLink[];
    onNavigate?: () => void;
}) {
    if (links.length === 0) return null;
    return (
        <>
            <div className="ql-lab">Quick links</div>
            {links.map((link) => (
                <QuickLinkRow
                    key={link.id}
                    link={link}
                    onNavigate={onNavigate}
                />
            ))}
        </>
    );
}

function NavItems({
    items,
    activeId,
    onNavigate,
}: {
    items: ReadonlyArray<ReadonlyArray<EventNavItem>>;
    activeId: string;
    onNavigate: (item: EventNavItem) => void;
}) {
    return (
        <>
            {items.map((cluster, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <div className="esep" />}
                    {cluster.map((item) => {
                        const Icon = AREA_ICONS[item.id];
                        return (
                            <button
                                key={item.id}
                                className={
                                    item.id === activeId
                                        ? 'row active'
                                        : 'row'
                                }
                                title={item.label}
                                aria-label={item.label}
                                onClick={() => onNavigate(item)}
                            >
                                {Icon && <Icon />}
                                <span className="lbl">{item.label}</span>
                            </button>
                        );
                    })}
                </React.Fragment>
            ))}
        </>
    );
}

export default function EventShell({
    orgInitials,
    orgLogo,
    eventName,
    eventSub,
    live,
    quickLinks,
    nav,
    activeSection,
    children,
}: {
    orgInitials: string;
    orgLogo?: string;
    eventName: string;
    eventSub?: string;
    live?: boolean;
    quickLinks: EventQuickLink[];
    nav: ReadonlyArray<ReadonlyArray<EventNavItem>>;
    activeSection: string;
    children: React.ReactNode;
}) {
    const navigate = useNavigate();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [findOpen, setFindOpen] = useState(false);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setFindOpen((v) => !v);
            }
            if (e.key === 'Escape') {
                setFindOpen(false);
                setSheetOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const go = (item: EventNavItem) => {
        setSheetOpen(false);
        navigate(item.to);
    };

    const eventCard = (
        <div className="ecard">
            <span className="eav">
                {(eventName.replace(/[^A-Za-z]/g, '').slice(0, 1) ||
                    eventName.slice(0, 1) ||
                    '·').toUpperCase()}
            </span>
            <span className="en">
                <span className="nm" title={eventName}>
                    {eventName}
                </span>
                {(eventSub || live) && (
                    <span className="sub">
                        {live && <span className="dot pulse" />}
                        {live ? `Live · ${eventSub ?? ''}` : eventSub}
                    </span>
                )}
            </span>
        </div>
    );

    return createPortal(
        <div className="psv3 col">
            <div className="etop">
                <span className="om">
                    {orgLogo ? (
                        <img
                            src={orgLogo}
                            className={orgLogo === smileMark ? 'app-mark' : undefined}
                            alt="Organization"
                        />
                    ) : (
                        orgInitials
                    )}
                </span>
                <span className="sep" />
                <button className="back" onClick={() => navigate('/activations')}>
                    <IconBack />
                    All activations
                </button>
                <button
                    className="ic"
                    aria-label="Find (⌘K)"
                    title="Find (⌘K)"
                    onClick={() => setFindOpen(true)}
                >
                    <IconSearch />
                </button>
            </div>
            <div className="ebody">
                <aside className="erail">
                    {eventCard}
                    <QuickLinks links={quickLinks} />
                    <div className="esep" />
                    <NavItems
                        items={nav}
                        activeId={activeSection}
                        onNavigate={go}
                    />
                </aside>
                <section className="esection">
                    {/* name the event even when the rail is slim or hidden */}
                    {activeSection === 'overview' ? (
                        <div className="emhead">
                            <h1>
                                {eventName}
                                {live && <span className="dot pulse" aria-label="Live" />}
                            </h1>
                            {eventSub && <p>{eventSub}</p>}
                        </div>
                    ) : (
                        /* sub-pages own their title; the event is context */
                        <p className="emhead sm">
                            {live && <span className="dot pulse" aria-label="Live" />}
                            {eventName}
                            {eventSub && <span> · {eventSub}</span>}
                        </p>
                    )}
                    {children}
                </section>
            </div>

            {/* mobile rendering */}
            {!sheetOpen && !findOpen && (
                <div className="fmenu">
                    <button className="find" onClick={() => setFindOpen(true)}>
                        <IconSearch />
                        Find
                    </button>
                    <span className="vsep" />
                    <button
                        className="mbtn"
                        aria-label="Menu"
                        title="Menu"
                        onClick={() => setSheetOpen(true)}
                    >
                        <IconMenu />
                    </button>
                </div>
            )}
            {sheetOpen && (
                <>
                    <div className="sheet">
                        <div className="chips">
                            <span className="chip">
                                <span className="om" style={{ width: 18, height: 18, fontSize: 8, borderRadius: 5 }}>
                                    {orgInitials}
                                </span>
                                {eventName}
                            </span>
                        </div>
                        <nav className="nav">
                            <button
                                className="row"
                                style={{ color: 'var(--text-tertiary)' }}
                                onClick={() => navigate('/activations')}
                            >
                                <IconBack />
                                All activations
                            </button>
                            <div className="gap" style={{ height: 8 }} />
                            <QuickLinks
                                links={quickLinks}
                                onNavigate={() => setSheetOpen(false)}
                            />
                            <div className="esep" />
                            <NavItems
                                items={nav}
                                activeId={activeSection}
                                onNavigate={go}
                            />
                        </nav>
                    </div>
                    <div className="fmenu">
                        <button
                            className="find"
                            onClick={() => {
                                setSheetOpen(false);
                                setFindOpen(true);
                            }}
                        >
                            <IconSearch />
                            Find
                        </button>
                        <span className="vsep" />
                        <button
                            className="mbtn"
                            aria-label="Close menu"
                            title="Close menu"
                            onClick={() => setSheetOpen(false)}
                        >
                            <IconClose />
                        </button>
                    </div>
                </>
            )}
            {findOpen && <Find onClose={() => setFindOpen(false)} />}
        </div>,
        document.body,
    );
}
